"use client";

import type { Key } from "@react-types/shared";
import { ChevronDown } from "@untitledui/icons";
import type { FC, ReactNode, Ref, RefAttributes } from "react";
import { isValidElement } from "react";
import type {
	SelectProps as AriaSelectProps,
	SelectRenderProps,
} from "react-aria-components";
import {
	Button as AriaButton,
	ListBox as AriaListBox,
	Select as AriaSelect,
	SelectValue as AriaSelectValue,
} from "react-aria-components";
import { Avatar } from "@/components/base/avatar/avatar";
import { HintText } from "@/components/base/input/hint-text";
import { Label } from "@/components/base/input/label";
import { cx } from "@/utils/cx";
import { isReactComponent } from "@/utils/is-react-component";
import { ComboBox } from "./combobox";
import { Popover } from "./popover";
import { SelectItem } from "./select-item";
import { SelectContext, type SelectItemType, sizes } from "./select-shared";
import { useSelectClearOnReselect } from "./use-select-clear-on-reselect";

type CommonProps = {
	/** Helper text displayed below the input. */
	hint?: string;
	/** Field label displayed above the input. */
	label?: string;
	/** Tooltip text for the help icon next to the label. */
	tooltip?: string;
	/**
	 * The size of the component.
	 * @default "md"
	 */
	size?: "sm" | "md" | "lg";
	/** Placeholder text when no value is selected. */
	placeholder?: string;
	/** Whether to hide the required indicator from the label. */
	hideRequiredIndicator?: boolean;
};

export {
	SelectContext,
	type SelectItemType,
	sizes,
} from "./select-shared";

export interface SelectProps
	extends Omit<AriaSelectProps<SelectItemType>, "children" | "items">,
		RefAttributes<HTMLDivElement>,
		CommonProps {
	items?: SelectItemType[];
	popoverClassName?: string;
	icon?: FC | ReactNode;
	children: ReactNode | ((item: SelectItemType) => ReactNode);
	/**
	 * When true (default), clicking the already-selected option clears the value.
	 * React Aria Select does not allow empty selection in single mode, so this is handled by the component.
	 */
	isClearable?: boolean;
}

type SelectValueProps = {
	isOpen: boolean;
	size: "sm" | "md" | "lg";
	isFocused: boolean;
	isDisabled: boolean;
	placeholder?: string;
	ref?: Ref<HTMLButtonElement>;
	icon?: FC | ReactNode;
	items?: SelectItemType[];
	selectedKey?: Key | null;
};

function resolveSelectedItem(
	collectionItem: SelectItemType | null | undefined,
	items: SelectItemType[] | undefined,
	selectedKey: Key | null | undefined
): SelectItemType | undefined {
	if (collectionItem?.label) {
		return collectionItem;
	}
	if (selectedKey === null || selectedKey === undefined || selectedKey === "") {
		return collectionItem ?? undefined;
	}
	return (
		items?.find((item) => String(item.id) === String(selectedKey)) ??
		collectionItem ??
		undefined
	);
}

const SelectValue = ({
	isOpen,
	isFocused,
	isDisabled,
	size,
	placeholder,
	icon,
	ref,
	items,
	selectedKey,
}: SelectValueProps) => {
	const renderLeadingVisual = (selectedItem?: SelectItemType | null) => {
		const iconNode = selectedItem?.icon || icon;

		if (selectedItem?.avatarUrl) {
			return (
				<Avatar
					alt={selectedItem.label}
					className={cx(size === "sm" && "size-5")}
					size="xs"
					src={selectedItem.avatarUrl}
				/>
			);
		}

		if (isReactComponent(iconNode)) {
			const IconComponent = iconNode;
			return <IconComponent aria-hidden="true" data-icon />;
		}

		if (isValidElement(iconNode)) {
			return iconNode;
		}

		return null;
	};

	return (
		<AriaButton
			className={cx(
				"relative flex w-full cursor-pointer items-center rounded-lg bg-primary shadow-xs outline-hidden ring-1 ring-primary ring-inset transition duration-100 ease-linear",
				"group-data-[invalid=true]:ring-border-error_subtle",
				(!!isFocused || !!isOpen) && "ring-2 ring-brand",
				!!isDisabled && "cursor-not-allowed! bg-disabled_subtle text-disabled"
			)}
			ref={ref}
		>
			<AriaSelectValue<SelectItemType>
				className={(state) =>
					cx(
						"flex h-max w-full items-center justify-start gap-2 truncate text-left align-middle",

						sizes[size].root,

						// With icon
						(!!state.selectedItems[0]?.icon || !!icon) && sizes[size].withIcon,

						// Icon styles
						"*:data-icon:size-5 *:data-icon:shrink-0 *:data-icon:text-fg-quaternary in-disabled:*:data-icon:text-fg-disabled"
					)
				}
			>
				{(state) => {
					const selectedItem = resolveSelectedItem(
						state.selectedItems[0],
						items,
						selectedKey
					);

					return (
						<>
							{renderLeadingVisual(selectedItem)}

							{selectedItem ? (
								<section
									className={cx(
										"flex w-full gap-2 truncate",
										sizes[size].textContainer
									)}
								>
									<p
										className={cx(
											"truncate font-medium text-md text-primary",
											sizes[size].text
										)}
									>
										{selectedItem?.label}
									</p>
									{!!selectedItem?.supportingText && (
										<p
											className={cx("text-md text-tertiary", sizes[size].text)}
										>
											{selectedItem.supportingText}
										</p>
									)}
								</section>
							) : (
								<p className={cx("text-md text-placeholder", sizes[size].text)}>
									{placeholder}
								</p>
							)}

							<ChevronDown
								aria-hidden="true"
								className={cx(
									"ml-auto shrink-0 text-fg-quaternary",
									size === "sm" ? "size-4 stroke-[2.5px]" : "size-5"
								)}
							/>
						</>
					);
				}}
			</AriaSelectValue>
		</AriaButton>
	);
};

const Select = ({
	placeholder = "Select",
	icon,
	size = "md",
	children,
	items,
	label,
	hint,
	tooltip,
	hideRequiredIndicator,
	className,
	isClearable = true,
	isOpen,
	onOpenChange,
	onChange,
	value,
	defaultValue,
	isDisabled,
	...rest
}: SelectProps) => {
	const hasVisibleLabel = !!label;
	const fallbackAriaLabel = placeholder || "Seleção";
	const ariaLabel =
		rest["aria-label"] ?? (hasVisibleLabel ? undefined : fallbackAriaLabel);
	const selectClassName = (
		state: SelectRenderProps & { defaultClassName: string | undefined }
	) =>
		cx(
			"group flex flex-col gap-1.5",
			typeof className === "function" ? className(state) : className
		);
	const clearOnReselect = useSelectClearOnReselect({
		defaultValue,
		isClearable,
		isDisabled,
		isOpen,
		onChange,
		onOpenChange,
		value,
	});

	return (
		<SelectContext.Provider value={{ size }}>
			<AriaSelect
				{...rest}
				aria-label={ariaLabel}
				className={selectClassName}
				isDisabled={isDisabled}
				isOpen={clearOnReselect.isOpen}
				key={clearOnReselect.selectInstanceId}
				onChange={clearOnReselect.onChange}
				onOpenChange={clearOnReselect.onOpenChange}
				value={clearOnReselect.value}
			>
				{(state) => (
					<>
						{!!label && (
							<Label
								isRequired={hideRequiredIndicator ? false : state.isRequired}
								tooltip={tooltip}
							>
								{label}
							</Label>
						)}

						<SelectValue
							{...state}
							{...{ size, placeholder }}
							icon={icon}
							items={items}
							selectedKey={clearOnReselect.value}
						/>

						<Popover className={rest.popoverClassName} size={size}>
							<div ref={clearOnReselect.listBoxRef}>
								<AriaListBox className="size-full outline-hidden" items={items}>
									{children}
								</AriaListBox>
							</div>
						</Popover>

						{!!hint && (
							<HintText
								className={cx(size === "sm" && "text-xs")}
								isInvalid={state.isInvalid}
							>
								{hint}
							</HintText>
						)}
					</>
				)}
			</AriaSelect>
		</SelectContext.Provider>
	);
};

const _Select = Select as typeof Select & {
	ComboBox: typeof ComboBox;
	Item: typeof SelectItem;
};
_Select.ComboBox = ComboBox;
_Select.Item = SelectItem;

export { _Select as Select };
